import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Save } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/services/api";

function StudentEditForm({ student, mutation }) {
  const [email, setEmail] = useState(student.email);
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    setNotice("");
    try {
      await mutation.mutateAsync({ userId: student.id, payload: { email, password: password || null } });
      setPassword("");
      setNotice("Credenciais atualizadas.");
    } catch (error) {
      setNotice(formatApiErrorDetail(error.response?.data?.detail));
    }
  };
  return (
    <form className="mt-4 grid gap-3 sm:grid-cols-[1fr_0.8fr_auto]" onSubmit={submit} data-testid={`admin-student-form-${student.id}`}>
      <input className="auth-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} data-testid={`admin-student-email-input-${student.id}`} />
      <input className="auth-input" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Nova senha opcional" data-testid={`admin-student-password-input-${student.id}`} />
      <button className="primary-action w-auto" type="submit" disabled={mutation.isPending} data-testid={`admin-student-save-button-${student.id}`}>Salvar <Save size={18} /></button>
      {notice && <p className="text-sm font-black text-slate-600 sm:col-span-3" data-testid={`admin-student-notice-${student.id}`}>{notice}</p>}
    </form>
  );
}

function StudentCard({ mutation, student }) {
  return (
    <article className="neo-card surface-neutral p-5" data-testid={`admin-student-card-${student.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid={`admin-student-email-${student.id}`}>{student.email}</p>
          <h2 className="font-display text-2xl font-black" data-testid={`admin-student-name-${student.id}`}>{student.name}</h2>
          <p className="mt-1 text-sm font-bold text-slate-600" data-testid={`admin-student-level-${student.id}`}>{student.level} · {student.phone || "sem telefone"}</p>
        </div>
        <span className="profile-badge" data-testid={`admin-student-daily-minutes-${student.id}`}>{student.daily_minutes} min hoje</span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-4" data-testid={`admin-student-metrics-${student.id}`}>
        <div className="mini-metric" data-testid={`admin-student-sessions-${student.id}`}>{student.sessions_completed} sessões</div>
        <div className="mini-metric" data-testid={`admin-student-total-minutes-${student.id}`}>{student.minutes_practiced} min</div>
        <div className="mini-metric" data-testid={`admin-student-score-${student.id}`}>{student.average_score}% média</div>
        <div className="mini-metric" data-testid={`admin-student-study-${student.id}`}>{student.study_completed} tópicos</div>
      </div>
      <StudentEditForm mutation={mutation} student={student} />
    </article>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-students"], queryFn: api.getAdminStudents, enabled: user?.role === "admin" });
  const mutation = useMutation({
    mutationFn: ({ userId, payload }) => api.updateAdminStudent(userId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-students"] }),
  });

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-page">
      <section className="page-title-row" data-testid="admin-title-section">
        <p className="section-kicker" data-testid="admin-page-kicker">área administrativa</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="admin-page-heading">Alunos, evolução e segurança.</h1>
        <p className="max-w-3xl text-base font-semibold text-slate-600 md:text-lg" data-testid="admin-page-description">Somente administradores podem ver logins, progresso, tempo diário e trocar email/senha de alunos.</p>
      </section>
      <section className="mt-8 grid gap-4 md:grid-cols-3" data-testid="admin-summary-grid">
        <article className="neo-card surface-yellow p-5" data-testid="admin-total-students-card"><ShieldCheck size={28} /><h2 className="mt-3 font-display text-4xl font-black" data-testid="admin-total-students-value">{data?.total_students || 0}</h2><p className="font-black text-slate-600">alunos</p></article>
        <article className="neo-card surface-mint p-5" data-testid="admin-active-today-card"><h2 className="font-display text-4xl font-black" data-testid="admin-active-today-value">{data?.active_students_today || 0}</h2><p className="font-black text-slate-600">ativos hoje</p></article>
        <article className="neo-card surface-blue p-5" data-testid="admin-minutes-today-card"><h2 className="font-display text-4xl font-black" data-testid="admin-minutes-today-value">{data?.total_minutes_today || 0}</h2><p className="font-black text-slate-600">minutos hoje</p></article>
      </section>
      {isLoading ? <div className="neo-card surface-neutral mt-8 p-6" data-testid="admin-loading-state">Carregando alunos...</div> : (
        <section className="mt-8 grid gap-5" data-testid="admin-students-list">
          {(data?.students || []).map((student) => <StudentCard key={student.id} mutation={mutation} student={student} />)}
        </section>
      )}
    </div>
  );
}